import { Link } from "@tanstack/react-router";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import {
  getButtonClassName,
  type ButtonChrome,
  type ButtonVariant,
} from "@/components/dashboard/monolithBoardStyle";

type ButtonBaseProps = {
  variant?: ButtonVariant;
  chrome?: ButtonChrome;
  className?: string;
  children: ReactNode;
};

type ButtonElementProps = ButtonBaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
  };

type ButtonLinkProps = ButtonBaseProps & {
  href: string;
  onClick?: () => void;
  role?: string;
  title?: string;
};

export type ButtonProps = ButtonElementProps | ButtonLinkProps;

function isLinkButtonProps(props: ButtonProps): props is ButtonLinkProps {
  return "href" in props && typeof props.href !== "undefined";
}

export function Button(props: ButtonLinkProps): ReactNode;
export function Button(props: ButtonElementProps): ReactNode;
export function Button(props: ButtonProps) {
  const resolvedClassName = getButtonClassName({
    variant: props.variant ?? "primary",
    chrome: props.chrome ?? "frame",
    className: props.className,
  });

  if (isLinkButtonProps(props)) {
    const {
      variant: _variant,
      chrome: _chrome,
      className: _className,
      children,
      href,
      ...linkProps
    } = props as ButtonLinkProps;
    return (
      <Link {...linkProps} to={href} className={resolvedClassName}>
        {children}
      </Link>
    );
  }

  const {
    variant: _variant,
    chrome: _chrome,
    className: _className,
    children,
    type = "button",
    ...buttonProps
  } = props;
  return (
    <button {...buttonProps} type={type} className={resolvedClassName}>
      {children}
    </button>
  );
}

export default Button;
